import { useEffect, useMemo, useState } from 'react';
import { Check } from 'lucide-react';
import { db } from '@/lib/db';
import type { Course, CourseTee, Member } from '@/lib/db/types';
import { getCourseCampus, getCourseComboLabel, sortCoursesByCombo } from '@/lib/courses/group';
import { clampCallTarget, expectedScoreForCourse } from '@/lib/shots/expected-score';
import { Sheet } from '@/components/shared/Sheet';

interface Props {
  member: Member;
  onClose: () => void;
  onSubmit: (data: { course_id: string; tee_id: string | null; target_score: number; notes?: string }) => void;
}

export function ProposeShotSheet({ member, onClose, onSubmit }: Props) {
  const [step, setStep] = useState<'course' | 'target'>('course');
  const [courses, setCourses] = useState<Course[]>([]);
  const [tees, setTees] = useState<CourseTee[]>([]);
  const [courseId, setCourseId] = useState('');
  const [teeId, setTeeId] = useState<string | null>(null);
  const [targetScore, setTargetScore] = useState(90);
  const [notes, setNotes] = useState('');

  useEffect(() => {
    void db.courses.list().then((list) => {
      const sorted = sortCoursesByCombo(list);
      setCourses(sorted);
      if (sorted[0]) setCourseId(sorted[0].id);
    });
  }, []);

  useEffect(() => {
    if (!courseId) return;
    void db.courses.tees(courseId).then((data) => {
      setTees(data);
      const white = data.find((t) => t.name.toLowerCase().includes('white')) || data[0];
      setTeeId(white?.id ?? null);
    });
  }, [courseId]);

  const course = courses.find((c) => c.id === courseId);
  const tee = tees.find((t) => t.id === teeId) ?? tees[0];
  const expected = useMemo(
    () => (course ? expectedScoreForCourse(member.handicap, course, tee) : 90),
    [course, tee, member.handicap],
  );

  useEffect(() => {
    setTargetScore(expected);
  }, [expected]);

  const validationHint = targetScore === expected
    ? `Maxed at your expected score (${expected}). Lower for a sharper call.`
    : targetScore > expected
      ? `Target exceeds your expected score (${expected}).`
      : `Calling ${expected - targetScore} stroke${expected - targetScore === 1 ? '' : 's'} better than expected.`;

  const canSubmit = targetScore >= 60 && targetScore <= expected;

  return (
    <Sheet title="Call Your Shot" onClose={onClose}>
      <div className="px-4 pb-8 pt-2">
        <div className="mb-4 flex gap-1">
          {(['course', 'target'] as const).map((s, i) => (
            <div
              key={s}
              className={`h-1 flex-1 rounded-full ${['course', 'target'].indexOf(step) >= i ? 'bg-gold' : 'bg-line'}`}
            />
          ))}
        </div>

        {step === 'course' && (
          <>
            <div className="mb-2 text-[11px] font-bold tracking-wide text-mute">STEP 1 · WHERE ARE YOU PLAYING?</div>
            <div className="mb-3.5 space-y-1.5">
              {courses.map((c) => {
                const on = courseId === c.id;
                return (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => setCourseId(c.id)}
                    className={`flex w-full items-center justify-between rounded-[10px] border px-3.5 py-2.5 text-left ${
                      on ? 'border-navy bg-navy text-paper' : 'border-line bg-paper text-ink'
                    }`}
                  >
                    <div>
                      <div className="font-serif text-[13px] font-bold">
                        {getCourseCampus(c)} · {getCourseComboLabel(c)}
                      </div>
                      <div className={`mt-0.5 text-[11px] ${on ? 'text-white/60' : 'text-mute'}`}>
                        Par {c.par}
                      </div>
                    </div>
                    {on && <Check size={16} className="text-gold" />}
                  </button>
                );
              })}
            </div>
            <button
              type="button"
              onClick={() => setStep('target')}
              className="w-full rounded-[10px] bg-gold py-3 text-[13px] font-bold text-navy"
            >
              Continue →
            </button>
          </>
        )}

        {step === 'target' && course && (
          <>
            <div className="mb-2 text-[11px] font-bold tracking-wide text-mute">STEP 2 · YOUR TARGET SCORE</div>

            <div className="mb-3.5 rounded-[14px] bg-navy p-3.5 text-center text-paper">
              <div className="text-[10px] font-bold tracking-[0.15em] text-white/60">YOUR CALL</div>
              <div className="mt-1.5 font-serif text-5xl font-bold leading-none text-gold">{targetScore}</div>
              <div className="mt-1.5 text-[11px] text-white/70">
                Expected from your handicap on {getCourseComboLabel(course)}: {expected}
              </div>
              <div className="mt-1.5 text-[10px] font-semibold text-gold-bright">
                Call must be {expected} or better — no calling a worse score than expected.
              </div>
            </div>

            <div className="mb-1.5 flex items-center gap-2.5">
              <button
                type="button"
                onClick={() => setTargetScore((t) => Math.max(60, t - 1))}
                className="rounded-[10px] border border-line bg-paper px-4 py-3 text-lg font-bold text-navy"
              >
                −
              </button>
              <input
                type="number"
                value={targetScore}
                min={60}
                max={expected}
                onChange={(e) => {
                  const v = parseInt(e.target.value, 10) || 0;
                  setTargetScore(clampCallTarget(v, expected));
                }}
                className={`flex-1 rounded-[10px] border bg-paper px-3 py-3 text-center text-[22px] font-bold text-navy outline-none ${
                  targetScore > expected ? 'border-bad' : 'border-line'
                }`}
              />
              <button
                type="button"
                disabled={targetScore >= expected}
                onClick={() => setTargetScore((t) => Math.min(expected, t + 1))}
                className={`rounded-[10px] border border-line px-4 py-3 text-lg font-bold ${
                  targetScore >= expected ? 'cursor-not-allowed opacity-50 text-mute' : 'bg-paper text-navy'
                }`}
              >
                +
              </button>
            </div>
            <div className="mb-3.5 text-center text-[10.5px] italic text-mute">{validationHint}</div>

            <div className="mb-1.5 text-[11px] font-bold tracking-wide text-mute">TRASH TALK (OPTIONAL)</div>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Coming off back-to-back 84s..."
              maxLength={140}
              className="mb-3.5 min-h-[60px] w-full resize-y rounded-[10px] border border-line bg-paper px-3.5 py-2.5 text-[13px] outline-none"
            />

            <div className="mb-3.5 rounded-lg bg-cream p-2.5 text-[11px] leading-[1.5] text-ink-soft">
              <strong className="text-navy">Scoring (per stroke vs. your call):</strong>{' '}
              Every stroke <strong className="text-good">under {targetScore}</strong>: <strong className="text-good">+10 pts</strong> each.{' '}
              Every stroke <strong className="text-bad">over {targetScore}</strong>: <strong className="text-bad">-5 pts</strong> each.{' '}
              Hit <strong>{targetScore}</strong> exactly: <strong>0 pts</strong>. Call carefully.
            </div>

            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setStep('course')}
                className="rounded-[10px] border border-line bg-cream px-4 py-3 text-[13px] font-semibold text-ink-soft"
              >
                ← Back
              </button>
              <button
                type="button"
                disabled={!canSubmit}
                onClick={() => onSubmit({
                  course_id: courseId,
                  tee_id: teeId,
                  target_score: clampCallTarget(targetScore, expected),
                  notes: notes.trim() || undefined,
                })}
                className={`flex-1 rounded-[10px] py-3 text-[13px] font-bold ${
                  canSubmit ? 'bg-gold text-navy' : 'cursor-not-allowed bg-line text-mute'
                }`}
              >
                Call My Shot
              </button>
            </div>
          </>
        )}
      </div>
    </Sheet>
  );
}
